import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { HardHat, Printer, ClipboardCheck, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, X, Loader2, CalendarDays, Tractor, Users, BookOpen, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const monday = (d = new Date()) => {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x.toISOString().slice(0, 10);
};
const shiftWeek = (w, n) => {
  const d = new Date(w + "T00:00:00");
  d.setDate(d.getDate() + n * 7);
  return d.toISOString().slice(0, 10);
};

const STATUS_STYLE = {
  ok: "bg-[#E8EFE6] text-[#1F3D34]", due_soon: "bg-amber-100 text-amber-800",
  overdue: "bg-red-100 text-red-700", never: "bg-stone-200 text-stone-600",
};

const REQ_LABELS = {
  en: {
    dpr_pesticide: "DPR Pesticide (Annual)", heat_annual: "Heat Illness (Annual)",
    harassment_annual: "Harassment (Annual)", harassment_refresher: "Harassment Refresher (6 mo)",
    tractor_annual: "Tractor Review (Annual)",
  },
  es: {
    dpr_pesticide: "Pesticidas DPR (Anual)", heat_annual: "Calor (Anual)",
    harassment_annual: "Acoso (Anual)", harassment_refresher: "Refuerzo de Acoso (6 m)",
    tractor_annual: "Repaso de Tractor (Anual)",
  },
};

export default function Training() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const isAdmin = user?.role === "admin";
  const canManage = ["admin", "supervisor"].includes(user?.role);
  const [tab, setTab] = useState("week");
  const [week, setWeek] = useState(monday());
  const [data, setData] = useState(null);
  const [recording, setRecording] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/training/week?week=${week}`);
    setData(data);
  };
  useEffect(() => { load(); }, [week]);

  const tabs = [["week", t("tr.this_week")]];
  if (isAdmin) tabs.push(["master", t("tr.master")]);
  if (canManage) tabs.push(["compliance", t("tr.compliance")]);
  tabs.push(["quarterly", t("tr.quarterly")]);
  tabs.push(["materials", t("tr.materials")]);
  tabs.push(["log", t("tr.log")]);

  return (
    <div className="space-y-6" data-testid="training-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{t("tr.title")}</h1>
        <p className="text-sm text-stone-500 mt-1 flex items-center gap-1"><HardHat size={15} /> {t("tr.sub")}</p>
      </div>
      <div className="flex gap-1 border border-stone-200 bg-white w-fit overflow-x-auto max-w-full">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`tr-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-xs font-bold uppercase whitespace-nowrap ${tab === k ? "bg-[#1F3D34] text-white" : "text-stone-600"}`}>{l}</button>
        ))}
      </div>

      {tab === "week" && (
        <>
          <div className="flex items-center gap-3">
            <button data-testid="tr-week-prev" onClick={() => setWeek(shiftWeek(week, -1))} className="h-10 w-10 border border-stone-300 bg-white flex items-center justify-center"><ChevronLeft size={16} /></button>
            <div className="font-bold" data-testid="tr-week-label">{t("tr.week_of")} {week}</div>
            <button data-testid="tr-week-next" onClick={() => setWeek(shiftWeek(week, 1))} className="h-10 w-10 border border-stone-300 bg-white flex items-center justify-center"><ChevronRight size={16} /></button>
          </div>
          {!data ? <div className="font-bold uppercase text-sm p-4">…</div> : (
            <>
              {data.flc_quarterly && data.flc_quarterly.status !== "completed" && data.flc_quarterly.status !== "upcoming" && (
                <div className={`border p-4 flex flex-wrap items-center justify-between gap-2 ${data.flc_quarterly.status === "overdue" ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`} data-testid="flc-quarterly-alert">
                  <div className={`font-bold text-sm flex items-center gap-2 ${data.flc_quarterly.status === "overdue" ? "text-red-700" : "text-amber-800"}`}>
                    <ShieldCheck size={16} />
                    {t("tr.q_alert").replace("{q}", `Q${data.flc_quarterly.quarter}`)}
                    {data.flc_quarterly.status === "due" && data.flc_quarterly.days_left != null && ` — ${data.flc_quarterly.days_left} ${t("tr.q_days_left")} (${data.flc_quarterly.end})`}
                    {data.flc_quarterly.status === "overdue" && ` — ${t("tr.overdue").toUpperCase()}`}
                  </div>
                  <button data-testid="flc-alert-goto" onClick={() => setTab("quarterly")} className="h-9 px-3 bg-[#1F3D34] text-white font-bold uppercase text-[10px]">{t("tr.quarterly")}</button>
                </div>
              )}
              {data.annual_summary?.length > 0 && (
                <div className="border border-amber-300 bg-amber-50 p-4" data-testid="annual-due-banner">
                  <div className="font-bold uppercase text-xs text-amber-800 flex items-center gap-2 mb-2"><AlertTriangle size={14} /> {t("tr.annual_due")}</div>
                  <div className="space-y-1">
                    {data.annual_summary.map((s) => (
                      <div key={s.code} className="text-sm">
                        <span className="font-bold">{REQ_LABELS[lang]?.[s.code] || s.code}:</span>{" "}
                        <span className="text-stone-700">{s.count} {t("tr.workers")} — {s.employees.slice(0, 6).join(", ")}{s.employees.length > 6 ? "…" : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid lg:grid-cols-2 gap-4">
                {["field", "tractor"].map((track) => (
                  <TrackCard key={track} track={track} info={data.tracks[track]} week={week} lang={lang} t={t}
                    onRecord={() => setRecording({ track, topic: data.tracks[track].topic })} />
                ))}
              </div>
              {data.conditions && (
                <div className="text-xs text-stone-500 font-mono" data-testid="week-conditions">
                  {t("tr.forecast")}: {data.conditions.max_temp_f != null ? `max ${data.conditions.max_temp_f}°F` : "—"}
                  {data.conditions.rain_in != null ? ` · ${t("tr.rain")} ${data.conditions.rain_in}"` : ""}
                  {data.conditions.wind_max_mph != null ? ` · ${t("tr.wind")} ${data.conditions.wind_max_mph} mph` : ""}
                  {data.conditions.has_spray ? ` · ${t("tr.spray_week")}` : ""}
                </div>
              )}
            </>
          )}
        </>
      )}

      {tab === "master" && isAdmin && <MasterSchedule t={t} />}
      {tab === "compliance" && canManage && <ComplianceMatrix t={t} />}
      {tab === "quarterly" && <QuarterlyTracker t={t} lang={lang} user={user} />}
      {tab === "materials" && <MaterialsLibrary t={t} lang={lang} />}
      {tab === "log" && <SessionLog t={t} />}

      {recording && (
        <RecordModal track={recording.track} topic={recording.topic} week={week} user={user} t={t}
          onClose={() => setRecording(null)} onDone={() => { setRecording(null); load(); }} />
      )}
    </div>
  );
}

function TrackCard({ track, info, week, lang, t, onRecord }) {
  const topic = info?.topic;
  if (!topic) return null;
  const dl = async (l) => {
    const res = await api.get(`/training/signoff-sheet?topic_id=${topic.id}&week=${week}&lang=${l}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `Training-SignOff-${topic.code}-${week}${l === "es" ? "-ES" : ""}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  return (
    <div className="bg-white border border-stone-200 shadow-sm" data-testid={`track-card-${track}`}>
      <div className="bg-[#1F3D34] text-white px-4 py-3 flex items-center justify-between">
        <div className="font-bold uppercase text-xs flex items-center gap-2">
          {track === "tractor" ? <Tractor size={15} /> : <Users size={15} />}
          {track === "tractor" ? t("tr.track_tractor") : t("tr.track_field")}
        </div>
        {info.completed
          ? <span className="text-[10px] font-bold uppercase bg-[#7C9673] px-2 py-1 flex items-center gap-1" data-testid={`track-done-${track}`}><CheckCircle2 size={12} /> {t("tr.done")}</span>
          : <span className="text-[10px] font-bold uppercase bg-white/15 px-2 py-1">{info.source === "admin" ? t("tr.by_admin") : t("tr.auto")}</span>}
      </div>
      <div className="p-4 space-y-3">
        <div>
          <div className="text-lg font-bold" style={{ fontFamily: "'Fraunces', serif" }}>{lang === "es" ? topic.title_es : topic.title}</div>
          <div className="text-xs text-stone-500 mt-0.5">{lang === "es" ? (info.reason_es || info.reason) : info.reason}{topic.required_by ? ` · ${topic.required_by}` : ""}</div>
        </div>
        <ul className="space-y-1.5">
          {(lang === "es" ? topic.points_es : topic.points).map((p, i) => (
            <li key={i} className="text-sm flex gap-2"><span className="text-[#7C9673] font-bold">•</span><span>{p}</span></li>
          ))}
        </ul>
        {topic.reference && <div className="text-[10px] text-stone-400 font-mono">{topic.reference}</div>}
        {topic.material_url && (
          <a href={topic.material_url} target="_blank" rel="noreferrer" data-testid={`material-link-${track}`}
            className="inline-flex items-center gap-1 text-xs font-bold uppercase text-[#1F3D34] underline underline-offset-2">
            <BookOpen size={13} /> {t("tr.read_material")} ↗
          </a>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <button data-testid={`signoff-en-${track}`} onClick={() => dl("en")} className="h-10 px-3 border border-stone-300 bg-white font-bold uppercase text-[11px] flex items-center gap-1"><Printer size={13} /> {t("tr.sheet_en")}</button>
          <button data-testid={`signoff-es-${track}`} onClick={() => dl("es")} className="h-10 px-3 border border-[#7C9673] text-[#1F3D34] bg-white font-bold uppercase text-[11px] flex items-center gap-1"><Printer size={13} /> {t("tr.sheet_es")}</button>
          {!info.completed && (
            <button data-testid={`record-btn-${track}`} onClick={onRecord} className="h-10 px-3 bg-[#1F3D34] text-white font-bold uppercase text-[11px] flex items-center gap-1"><ClipboardCheck size={13} /> {t("tr.record")}</button>
          )}
        </div>
        {info.session && (
          <div className="text-xs text-[#1F3D34] bg-[#E8EFE6] p-2" data-testid={`session-info-${track}`}>
            ✓ {info.session.date} — {info.session.trainer} · {info.session.attendee_names?.length} {t("tr.attendees")}
          </div>
        )}
      </div>
    </div>
  );
}

function RecordModal({ track, topic, week, user, t, onClose, onDone }) {
  const [employees, setEmployees] = useState([]);
  const [sel, setSel] = useState([]);
  const [dateV, setDateV] = useState(new Date().toISOString().slice(0, 10));
  const [trainer, setTrainer] = useState(user?.name || "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/employees").then(({ data }) => {
      const act = data;
      const isTr = (e) => (e.category || "").toLowerCase().startsWith("tractor");
      const isSup = (e) => /supervisor|foreman|crew lead/i.test(e.category || "");
      if (track === "tractor") {
        setEmployees(act.filter(isTr).concat(act.filter((e) => !isTr(e))));
        setSel(act.filter(isTr).map((e) => e.id));
      } else if (track === "supervisors") {
        setEmployees(act.filter(isSup).concat(act.filter((e) => !isSup(e))));
        setSel(act.filter(isSup).map((e) => e.id));
      } else {
        setEmployees(act);
        setSel([]);
      }
    });
  }, [track]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const save = async () => {
    if (!sel.length) { toast.error(t("tr.need_attendees")); return; }
    setBusy(true);
    try {
      await api.post("/training/sessions", { topic_id: topic.id, week_start: week, track, date: dateV, trainer, attendee_ids: sel, notes: notes || null });
      toast.success(t("tr.recorded"));
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="record-modal">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>{t("tr.record")} — {topic.title}</h3>
          <button onClick={onClose} data-testid="record-close"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><div className="text-[10px] font-bold uppercase text-stone-500 mb-1">{t("tr.date")}</div>
            <input type="date" data-testid="record-date" value={dateV} onChange={(e) => setDateV(e.target.value)} className="w-full h-11 px-3 border border-stone-300" /></div>
          <div><div className="text-[10px] font-bold uppercase text-stone-500 mb-1">{t("tr.trainer")}</div>
            <input data-testid="record-trainer" value={trainer} onChange={(e) => setTrainer(e.target.value)} className="w-full h-11 px-3 border border-stone-300" /></div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase text-stone-500 mb-1">{t("tr.attendees")} ({sel.length})</div>
          <div className="border border-stone-200 max-h-56 overflow-y-auto divide-y divide-stone-100">
            {employees.map((e) => (
              <label key={e.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-stone-50">
                <input type="checkbox" data-testid={`attendee-${e.id}`} checked={sel.includes(e.id)} onChange={() => toggle(e.id)} className="h-5 w-5 accent-[#1F3D34]" />
                <span className="text-sm">{e.name}</span>
                <span className="text-[10px] uppercase text-stone-400 ml-auto">{e.category}</span>
              </label>
            ))}
          </div>
        </div>
        <input data-testid="record-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t("tr.notes")} className="w-full h-11 px-3 border border-stone-300 text-sm" />
        <button data-testid="record-save" onClick={save} disabled={busy}
          className="w-full h-12 bg-[#1F3D34] text-white font-bold uppercase text-sm flex items-center justify-center gap-2 disabled:opacity-40">
          {busy && <Loader2 size={16} className="animate-spin" />} {t("tr.save_session")}
        </button>
      </div>
    </div>
  );
}

function MasterSchedule({ t }) {
  const [start, setStart] = useState(monday());
  const [rows, setRows] = useState(null);
  const [topics, setTopics] = useState([]);
  const load = async () => {
    const [m, tp] = await Promise.all([api.get(`/training/master?start=${start}&weeks=12`), api.get("/training/topics")]);
    setRows(m.data); setTopics(tp.data);
  };
  useEffect(() => { load(); }, [start]);
  const setTopic = async (w, track, topic_id) => {
    if (!topic_id) { await api.delete(`/training/schedule/${w}/${track}`); }
    else { await api.post("/training/schedule", { week_start: w, track, topic_id }); }
    await load();
  };
  const opts = (track) => topics.filter((x) => x.category === "tailgate" && (x.track === track || x.track === "all"));
  if (!rows) return <div className="font-bold uppercase text-sm p-4">…</div>;
  return (
    <div className="bg-white border border-stone-200" data-testid="master-schedule">
      <div className="flex items-center justify-between p-3 border-b border-stone-200">
        <div className="text-sm font-bold uppercase flex items-center gap-2"><CalendarDays size={15} /> {t("tr.master")}</div>
        <div className="flex gap-2">
          <button data-testid="master-prev" onClick={() => setStart(shiftWeek(start, -12))} className="h-9 w-9 border border-stone-300 flex items-center justify-center"><ChevronLeft size={14} /></button>
          <button data-testid="master-next" onClick={() => setStart(shiftWeek(start, 12))} className="h-9 w-9 border border-stone-300 flex items-center justify-center"><ChevronRight size={14} /></button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase text-stone-500 border-b border-stone-200">
            <th className="p-3">{t("tr.week")}</th><th className="p-3">{t("tr.track_field")}</th><th className="p-3">{t("tr.track_tractor")}</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.week_start} className="border-b border-stone-100" data-testid={`master-row-${r.week_start}`}>
                <td className="p-3 font-mono text-xs whitespace-nowrap">{r.week_start}</td>
                {["field", "tractor"].map((track) => (
                  <td key={track} className="p-2">
                    <div className="flex items-center gap-2">
                      <select data-testid={`master-${track}-${r.week_start}`} value={r[track].scheduled?.topic_id || ""}
                        onChange={(e) => setTopic(r.week_start, track, e.target.value)}
                        className="h-10 px-2 border border-stone-200 bg-white text-xs w-full max-w-xs">
                        <option value="">{t("tr.auto_weather")}</option>
                        {opts(track).map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                      </select>
                      {r[track].completed && <CheckCircle2 size={16} className="text-[#7C9673] shrink-0" />}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ComplianceMatrix({ t }) {
  const { lang } = useI18n();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/training/compliance").then(({ data }) => setRows(data)); }, []);
  if (!rows) return <div className="font-bold uppercase text-sm p-4">…</div>;
  const byEmp = {};
  rows.forEach((r) => {
    byEmp[r.employee_id] = byEmp[r.employee_id] || { name: r.employee_name, category: r.category, reqs: {} };
    byEmp[r.employee_id].reqs[r.code] = r;
  });
  const codes = ["dpr_pesticide", "heat_annual", "harassment_annual", "harassment_refresher", "tractor_annual"];
  return (
    <div className="bg-white border border-stone-200 overflow-x-auto" data-testid="compliance-matrix">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[10px] uppercase text-stone-500 border-b border-stone-200">
          <th className="p-3">{t("tr.employee")}</th>
          {codes.map((c) => <th key={c} className="p-3">{REQ_LABELS[lang]?.[c] || c}</th>)}
        </tr></thead>
        <tbody>
          {Object.entries(byEmp).map(([id, e]) => (
            <tr key={id} className="border-b border-stone-100" data-testid={`compliance-row-${id}`}>
              <td className="p-3 font-semibold whitespace-nowrap">{e.name}<div className="text-[10px] text-stone-400 uppercase">{e.category}</div></td>
              {codes.map((c) => {
                const r = e.reqs[c];
                if (!r) return <td key={c} className="p-3 text-stone-300">—</td>;
                return (
                  <td key={c} className="p-3">
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 ${STATUS_STYLE[r.status]}`}>
                      {r.status === "never" ? t("tr.never") : r.status === "overdue" ? t("tr.overdue") : r.status === "due_soon" ? t("tr.due_soon") : "OK"}
                    </span>
                    {r.last_date && <div className="text-[10px] text-stone-500 font-mono mt-1">{r.last_date}</div>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QuarterlyTracker({ t, lang, user }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [recording, setRecording] = useState(null);
  const load = async () => {
    const { data } = await api.get(`/training/quarterly?year=${year}`);
    setData(data);
  };
  useEffect(() => { load(); }, [year]);
  if (!data) return <div className="font-bold uppercase text-sm p-4">…</div>;
  const topic = data.topic;
  const chip = {
    completed: "bg-[#E8EFE6] text-[#1F3D34]", due: "bg-amber-100 text-amber-800",
    overdue: "bg-red-100 text-red-700", upcoming: "bg-stone-200 text-stone-600",
  };
  const label = { completed: t("tr.done"), due: t("tr.q_due"), overdue: t("tr.overdue"), upcoming: t("tr.q_upcoming") };
  const dl = async (l, q) => {
    const res = await api.get(`/training/signoff-sheet?topic_id=${topic.id}&week=${year}-Q${q}&lang=${l}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `FLC-Quarterly-Q${q}-${year}${l === "es" ? "-ES" : ""}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4" data-testid="quarterly-tracker">
      <div className="bg-white border border-stone-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-bold" style={{ fontFamily: "'Fraunces', serif" }}>{lang === "es" ? topic.title_es : topic.title}</div>
            <div className="text-xs text-stone-500 mt-0.5">{topic.required_by} · {topic.reference}</div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="q-year-prev" onClick={() => setYear(year - 1)} className="h-9 w-9 border border-stone-300 flex items-center justify-center"><ChevronLeft size={14} /></button>
            <div className="font-bold font-mono" data-testid="q-year-label">{year}</div>
            <button data-testid="q-year-next" onClick={() => setYear(year + 1)} className="h-9 w-9 border border-stone-300 flex items-center justify-center"><ChevronRight size={14} /></button>
          </div>
        </div>
        <ul className="mt-3 space-y-1.5">
          {(lang === "es" ? topic.points_es : topic.points).map((p, i) => (
            <li key={i} className="text-sm flex gap-2"><span className="text-[#7C9673] font-bold">•</span><span>{p}</span></li>
          ))}
        </ul>
        {topic.material_url && (
          <a href={topic.material_url} target="_blank" rel="noreferrer" data-testid="material-link-quarterly"
            className="inline-flex items-center gap-1 text-xs font-bold uppercase text-[#1F3D34] underline underline-offset-2 mt-3">
            <BookOpen size={13} /> {t("tr.read_material")} ↗
          </a>
        )}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {data.quarters.map((q) => (
          <div key={q.quarter} className="bg-white border border-stone-200" data-testid={`quarter-card-${q.quarter}`}>
            <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between">
              <div className="font-black text-lg" style={{ fontFamily: "'Fraunces', serif" }}>Q{q.quarter}</div>
              <span className={`text-[10px] font-bold uppercase px-2 py-1 ${chip[q.status]}`} data-testid={`quarter-status-${q.quarter}`}>{label[q.status]}</span>
            </div>
            <div className="p-4 space-y-2">
              <div className="text-[10px] font-mono text-stone-500">{q.start} → {q.end}</div>
              {q.status === "due" && q.days_left != null && <div className="text-xs font-bold text-amber-700">{q.days_left} {t("tr.q_days_left")}</div>}
              {q.session ? (
                <div className="text-xs bg-[#E8EFE6] text-[#1F3D34] p-2">
                  ✓ {q.session.date} — {q.session.trainer}<br />{(q.session.attendee_names || []).join(", ")}
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  <button data-testid={`q-signoff-${q.quarter}`} onClick={() => dl("en", q.quarter)} className="h-9 px-2 border border-stone-300 bg-white font-bold uppercase text-[10px] flex items-center gap-1"><Printer size={12} /> EN</button>
                  <button data-testid={`q-signoff-es-${q.quarter}`} onClick={() => dl("es", q.quarter)} className="h-9 px-2 border border-[#7C9673] bg-white font-bold uppercase text-[10px] flex items-center gap-1"><Printer size={12} /> ES</button>
                  {q.status !== "upcoming" && (
                    <button data-testid={`q-record-${q.quarter}`} onClick={() => setRecording(q)} className="h-9 px-2 bg-[#1F3D34] text-white font-bold uppercase text-[10px] flex items-center gap-1"><ClipboardCheck size={12} /> {t("tr.record")}</button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {recording && (
        <RecordModal track="supervisors" topic={topic} week={recording.start} user={user} t={t}
          onClose={() => setRecording(null)} onDone={() => { setRecording(null); load(); }} />
      )}
    </div>
  );
}

function MaterialsLibrary({ t, lang }) {
  const [cats, setCats] = useState(null);
  useEffect(() => { api.get("/training/materials").then(({ data }) => setCats(data)); }, []);
  if (!cats) return <div className="font-bold uppercase text-sm p-4">…</div>;
  return (
    <div className="space-y-4" data-testid="materials-library">
      {cats.map((c) => (
        <div key={c.category} className="bg-white border border-stone-200" data-testid={`materials-cat-${c.category.split(" ")[0]}`}>
          <div className="bg-[#1F3D34] text-white px-4 py-3 flex items-center justify-between gap-3">
            <div className="font-bold uppercase text-xs">{lang === "es" ? c.category_es : c.category}</div>
            {c.url && <a href={c.url} target="_blank" rel="noreferrer" className="text-[10px] font-bold uppercase underline text-[#DDE7DA] whitespace-nowrap">{t("tr.open_source")}</a>}
          </div>
          <div className="p-4 space-y-3">
            <p className="text-sm text-stone-600">{lang === "es" ? c.note_es : c.note}</p>
            {c.items.length > 0 && (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[10px] uppercase text-stone-500 border-b border-stone-200">
                  <th className="py-2 pr-3">{t("tr.mat_code")}</th><th className="py-2 pr-3">{t("tr.mat_title")}</th><th className="py-2">{t("tr.mat_who")}</th>
                </tr></thead>
                <tbody>
                  {c.items.map((it) => (
                    <tr key={it.code + it.title} className="border-b border-stone-100">
                      <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap font-bold text-[#1F3D34]">{it.code}</td>
                      <td className="py-2 pr-3">{it.title}</td>
                      <td className="py-2 text-xs text-stone-500">{it.who}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function SessionLog({ t }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/training/sessions").then(({ data }) => setRows(data)); }, []);
  if (!rows) return <div className="font-bold uppercase text-sm p-4">…</div>;
  return (
    <div className="bg-white border border-stone-200 overflow-x-auto" data-testid="session-log">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[10px] uppercase text-stone-500 border-b border-stone-200">
          <th className="p-3">{t("tr.date")}</th><th className="p-3">{t("tr.topic")}</th><th className="p-3">{t("tr.trainer")}</th><th className="p-3">{t("tr.attendees")}</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-stone-400 font-bold uppercase">{t("tr.no_sessions")}</td></tr>}
          {rows.map((s) => (
            <tr key={s.id} className="border-b border-stone-100" data-testid={`session-row-${s.id}`}>
              <td className="p-3 font-mono text-xs">{s.date}</td>
              <td className="p-3">{s.topic_title}<div className="text-[10px] uppercase text-stone-400">{s.track}</div></td>
              <td className="p-3">{s.trainer}</td>
              <td className="p-3 text-xs">{(s.attendee_names || []).join(", ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
